import React from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/common/SEO';
import { getAllBrands, getProductsByBrand } from '../data/brandsData';
import './Brands.css';

const Brands: React.FC = () => {
  const brands = getAllBrands();

  return (
    <>
      <SEO
        title="Our Brands - SKYSUN & SKYTION"
        description="Explore Skytion Enterprises' multi-brand portfolio. Premium SKYSUN and economy SKYTION tissue products for every need."
        keywords="skysun brand, skytion brand, tissue brands, tissue products, multi-brand portfolio"
      />

      {/* Hero Section */}
      <section className="brands-hero py-5">
        <Container>
          <Row className="text-center py-5">
            <Col lg={8} className="mx-auto brands-hero-content">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Badge bg="light" text="dark" className="mb-3 px-4 py-2">Our Brand Portfolio</Badge>
                <h1 className="display-3 mb-4">Two Brands, One Commitment</h1>
                <p className="lead mb-4">
                  Skytion Enterprises offers a comprehensive multi-brand strategy to serve diverse market segments.
                  From premium retail to budget-friendly bulk solutions, we have the right brand for your needs.
                </p>
                <p className="text-muted">
                </p>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Brand Cards */}
      <section className="py-5">
        <Container>
          <Row className="g-4">
            {brands.map((brand, index) => {
              const productCount = getProductsByBrand(brand.name as any).length;
              const brandClass = `brand-card-${brand.id}`;

              return (
                <Col lg={4} md={6} key={brand.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className={`brand-card ${brandClass} h-100`}>
                      <Card.Body className="p-4">
                        <div className="mb-4">
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div className={`brand-icon brand-icon-${brand.id} mb-3`}>
                              {brand.id === 'skysun' && '★'}
                              {brand.id === 'skytion' && '●'}
                            </div>
                          </div>
                          <h2 className="display-5 mb-2 fw-bold" style={{ color: brand.colors.primary }}>
                            {brand.displayName}
                          </h2>
                          <Badge className={`badge-${brand.id === 'skysun' ? 'premium' : 'economy'}`}>
                            {brand.positioning}
                          </Badge>
                          <p className="text-muted fst-italic mt-2 mb-0">"{brand.tagline}"</p>
                        </div>

                        <p className="mb-4">{brand.description}</p>

                        <div className="mb-4">
                          <h6 className="fw-bold mb-2">Target Markets:</h6>
                          <div className="market-badge-group">
                            {brand.targetMarket.map((market, idx) => (
                              <span key={idx} className="market-badge">
                                {market}
                              </span>
                            ))}
                          </div>
                        </div>

                        {brand.certifications && brand.certifications.length > 0 && (
                          <div className="mb-4">
                            <h6 className="fw-bold mb-2">Certifications:</h6>
                            <div className="certification-badge-group">
                              {brand.certifications.map((cert, idx) => (
                                <span key={idx} className="certification-badge">
                                  ✓ {cert}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="mb-4 product-count-display">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-bold">{productCount}</span>
                            <span className="text-muted">Product{productCount !== 1 ? 's' : ''} Available</span>
                          </div>
                        </div>

                        <Link
                          to={`/brands/${brand.id}`}
                          className="btn w-100"
                          style={{
                            backgroundColor: brand.colors.primary,
                            borderColor: brand.colors.primary,
                            color: 'white'
                          }}
                        >
                          Explore {brand.displayName} Products
                        </Link>
                      </Card.Body>
                    </Card>
                  </motion.div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>

      {/* Brand Positioning */}
      <section className="py-5 brand-positioning-section">
        <Container>
          <Row className="mb-5">
            <Col lg={8} className="mx-auto text-center">
              <h2 className="display-5 mb-4">Strategic Brand Positioning</h2>
              <p className="lead">
                Our two-tier brand strategy ensures we meet the diverse needs of tissue product consumers
                across retail, premium, and economy segments.
              </p>
            </Col>
          </Row>

          <Row className="g-4">
            <Col md={4}>
              <div className="positioning-card">
                <div className="positioning-icon positioning-icon-premium mb-3">★</div>
                <h4 className="mb-3">SKYSUN</h4>
                <p className="text-muted">
                  Premium positioning for discerning consumers who demand the highest quality.
                  Perfect for retail, hospitality, and export markets.
                </p>
              </div>
            </Col>

            <Col md={4}>
              <div className="positioning-card">
                <div className="positioning-icon positioning-icon-economy mb-3">●</div>
                <h4 className="mb-3">SKYTION</h4>
                <p className="text-muted">
                  Value-driven solutions for cost-conscious operations requiring high volume supply.
                  Best for bulk distribution and large-scale facilities.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quality Commitment */}
      <section className="py-5 quality-commitment-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={6}>
              <h2 className="display-5 mb-4">Unified Quality Standards</h2>
              <p className="lead mb-4">
                While our brands serve different market segments, all products are manufactured
                in our ISO-certified facility using consistent quality control processes.
              </p>
              <ul className="list-unstyled">
                <li className="mb-3">
                  <span className="text-success me-2 fw-bold fs-4">✓</span>
                  <strong>ISO 9001:2015 Certified Manufacturing</strong>
                </li>
                <li className="mb-3">
                  <span className="text-success me-2 fw-bold fs-4">✓</span>
                  <strong>Rigorous Quality Testing at Every Stage</strong>
                </li>
                <li className="mb-3">
                  <span className="text-success me-2 fw-bold fs-4">✓</span>
                  <strong>Sustainable Sourcing Practices</strong>
                </li>
                <li className="mb-3">
                  <span className="text-success me-2 fw-bold fs-4">✓</span>
                  <strong>State-of-the-Art Production Technology</strong>
                </li>
                <li className="mb-3">
                  <span className="text-success me-2 fw-bold fs-4">✓</span>
                  <strong>Consistent Supply Chain Management</strong>
                </li>
              </ul>
            </Col>
            <Col lg={6}>
              <Row className="g-3">
                <Col sm={6}>
                  <div className="quality-stat-card">
                    <h3 className="text-primary mb-2">2022</h3>
                    <p className="mb-0">Established</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="quality-stat-card">
                    <h3 className="text-success mb-2">2</h3>
                    <p className="mb-0">Brands</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="quality-stat-card">
                    <h3 className="text-info mb-2">10+</h3>
                    <p className="mb-0">Products</p>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="quality-stat-card">
                    <h3 className="text-warning mb-2">5</h3>
                    <p className="mb-0">Countries</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 brands-cta-section">
        <Container>
          <Row className="text-center brands-cta-content">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 mb-4">Find Your Perfect Brand</h2>
                <p className="lead mb-4">
                Whether you're looking for premium retail products or economical bulk options,
                we have a brand that fits your needs.
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link to="/contact" className="btn btn-light btn-lg">
                  Contact Sales Team
                </Link>
                <Link to="/products" className="btn btn-outline-light btn-lg">
                  View All Products
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Brands;
